import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import SEO from '../components/SEO';
export default function BlogAutomation() {
  useReveal();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  return (
    <div className="page">
      <SEO
  title="From Manual Work to Automation: A Real Business Shift | Arhatinfo"
  description="Automation is not about replacing systems overnight. Learn how businesses add automation layers on top of existing tools to reduce manual work and improve consistency."
  keywords="business automation, workflow automation, process automation, invoice automation, Django automation, Python automation, business digital transformation, automation layer"
  canonical="https://arhatinfo.com/blog/automation"
  og={{
    title:       "From Manual Work to Automation: A Real Business Shift",
    description: "Build better workflows around the systems and people you already have. A practical guide to business automation without replacing everything.",
    url:         "https://arhatinfo.com/blog/automation",
    image:       "https://arhatinfo.com/og-blog.png",
  }}
/>
      <article>

        {/* ── HERO ─────────────────────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg,var(--bg2),var(--bg3))',
          borderBottom: '1px solid var(--border-s)',
          padding: '80px 5vw 60px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position:'absolute', inset:0,
            backgroundImage:'linear-gradient(rgba(53,187,234,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(53,187,234,.04) 1px,transparent 1px)',
            backgroundSize:'50px 50px', pointerEvents:'none',
          }}/>
          <div className="wrap" style={{ position:'relative', zIndex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:'.5rem', marginBottom:'1.5rem', fontSize:'.8rem', fontWeight:500 }}>
              <Link to="/"     style={{ color:'var(--muted)', textDecoration:'none' }}>Home</Link>
              <span style={{ color:'var(--muted)' }}>›</span>
              <Link to="/blog" style={{ color:'var(--muted)', textDecoration:'none' }}>Blog</Link>
              <span style={{ color:'var(--muted)' }}>›</span>
              <span style={{ color:'var(--blue-hi)' }}>Business Automation</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'.75rem', marginBottom:'1.2rem', flexWrap:'wrap' }}>
              <span style={{
                fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase',
                color:'var(--blue-hi)', background:'rgba(53,187,234,.1)',
                border:'1px solid rgba(53,187,234,.25)', padding:'.28rem .9rem', borderRadius:20,
              }}>Business Automation</span>
              <span style={{ fontSize:'.78rem', fontWeight:500, color:'var(--muted)' }}>11 min read</span>
              <span style={{ color:'var(--muted)', fontSize:'.78rem' }}>·</span>
              <span style={{ fontSize:'.78rem', fontWeight:500, color:'var(--muted)' }}>May 2025</span>
            </div>
            <h1 style={{
              fontFamily:'var(--font)', fontSize:'clamp(1.9rem,4.5vw,3.2rem)',
              fontWeight:900, color:'var(--white)', lineHeight:1.1, letterSpacing:'-.02em',
              maxWidth:820, marginBottom:'.8rem', textShadow:'0 4px 20px rgba(0,0,0,.25)',
            }}>
              From Manual Work to Automation:{' '}
              <span style={{ color:'var(--blue-hi)' }}>A Real Business Shift</span>
            </h1>
            <p style={{ fontSize:'1rem', fontWeight:500, color:'var(--muted)', marginBottom:'1.5rem', maxWidth:640 }}>
              Integrating automation layers — without replacing systems — what that actually looks like in practice
            </p>
            <div style={{ display:'flex', alignItems:'center', gap:'.75rem' }}>
              <div style={{
                width:40, height:40, borderRadius:'50%', flexShrink:0,
                background:'linear-gradient(135deg,var(--blue-dark),var(--blue-deep))',
                border:'2px solid var(--blue-hi)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'.78rem', fontWeight:700, color:'var(--white)',
              }}>RJ</div>
              <div>
                <div style={{ fontSize:'.85rem', fontWeight:700, color:'var(--white)' }}>Rohit Jain</div>
                <div style={{ fontSize:'.75rem', fontWeight:500, color:'var(--muted)' }}>arhatinfo.com · Backend & AI Engineering</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── BODY ─────────────────────────────────────────────────── */}
        <div style={{ padding:'60px 5vw 80px' }}>
          <div style={{ maxWidth:780, margin:'0 auto' }}>

            <Lead>
              Most businesses do not fail because their people are not working hard enough.
              They struggle because too much important work still depends on manual steps,
              repeated decisions, spreadsheets, email follow-ups, copy-paste tasks, and
              people remembering what needs to happen next.
            </Lead>
            <Lead>
              Automation is not about replacing your entire system overnight. In real businesses,
              automation usually works best when it is added as a layer on top of existing tools —
              keeping your current accounting software, CRM, inbox, spreadsheets, ERP, or internal
              portals, but connecting them with workflows that reduce manual effort.
            </Lead>

            {/* ── THE OLD WAY ── */}
            <SectionHeading>The Old Way: Manual Work Everywhere</SectionHeading>
            <Body>
              In many companies, a simple process looks like this — and it works when the
              business is small. But as volume grows, the same process becomes slow, expensive,
              and error-prone.
            </Body>

            {/* Diagram 1 — manual flow */}
            <div className="rv" style={{ margin:'1.8rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid var(--border-hi)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="200" viewBox="0 0 900 200" xmlns="http://www.w3.org/2000/svg">
                <rect width="900" height="200" rx="20" fill="#0c0e14"/>
                <defs>
                  <marker id="a1" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L8,3 z" fill="#6A99AB"/>
                  </marker>
                </defs>
                {[
                  { label:'Email\nRequest',   x:60  },
                  { label:'Manual\nReview',   x:245 },
                  { label:'Copy Data\nto Sheet', x:430 },
                  { label:'Update\nSystem',   x:615 },
                  { label:'Done\n(maybe)',    x:800 },
                ].map(({ label, x }, i, arr) => (
                  <g key={i}>
                    <rect x={x} y={68} width={145} height={64} rx="10" fill="#1a0e0e" stroke="#E74C3C" strokeWidth="1.5"/>
                    {label.split('\n').map((ln, li) => (
                      <text key={li} x={x+72} y={96 + li*20} textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#f87171">{ln}</text>
                    ))}
                    {i < arr.length - 1 && (
                      <line x1={x+145} y1={100} x2={arr[i+1].x} y2={100} stroke="#6A99AB" strokeWidth="1.5" markerEnd="url(#a1)"/>
                    )}
                  </g>
                ))}
                <text x="450" y="168" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#888">Every step depends on a person doing repetitive work correctly</text>
              </svg>
            </div>

            <Body>
              Someone forgets to update the sheet. Someone enters the wrong value. Someone misses
              an email. Someone approves something late. Someone spends hours preparing reports
              that should have been generated automatically. The problem is not the people.
              The problem is the process.
            </Body>

            {/* ── THE REAL SHIFT ── */}
            <SectionHeading>The Real Shift: Automation as a Layer</SectionHeading>
            <Body>
              A common mistake is thinking automation means replacing everything. In practice,
              good automation keeps the tools the business already uses and adds a workflow layer
              that connects them.
            </Body>

            {/* Diagram 2 — automation layer */}
            <div className="rv" style={{ margin:'1.8rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid var(--border-hi)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="280" viewBox="0 0 900 280" xmlns="http://www.w3.org/2000/svg">
                <rect width="900" height="280" rx="20" fill="#0c0e14"/>
                <defs>
                  <marker id="a2" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L8,3 z" fill="#35BBEA" fillOpacity="0.6"/>
                  </marker>
                </defs>
                <text x="450" y="40" textAnchor="middle" fontSize="18" fontFamily="Arial" fill="#eef2ff" fontWeight="700">Automation Layer on Top of Existing Systems</text>
                {/* automation layer */}
                <rect x="140" y="72" width="620" height="72" rx="14" fill="#0a2a18" stroke="#35BBEA" strokeWidth="2"/>
                <text x="450" y="102" textAnchor="middle" fontSize="16" fontFamily="Arial" fill="#35BBEA" fontWeight="700">Automation Workflow Layer</text>
                <text x="450" y="126" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#6A99AB">Rules  ·  APIs  ·  AI extraction  ·  Notifications  ·  Approvals  ·  Sync jobs</text>
                {/* existing systems */}
                {[
                  { label:'Email',      x:65  },
                  { label:'CRM',        x:255 },
                  { label:'Accounting', x:445 },
                  { label:'Database',   x:640 },
                ].map(({ label, x }, i) => (
                  <g key={i}>
                    <rect x={x} y={192} width={150} height={54} rx="10" fill="#111520" stroke="#35BBEA" strokeWidth="1.5"/>
                    <text x={x+75} y={224} textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff">{label}</text>
                    <line x1={x+75} y1={192} x2={x+75} y2={144} stroke="#35BBEA" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#a2)"/>
                  </g>
                ))}
              </svg>
            </div>

            <Body>
              This is important because most businesses cannot afford a full system replacement —
              it is risky, expensive, disruptive, and often unnecessary.
            </Body>
            <Callout>
              Instead of asking "Which system should we replace?" the better question is: where are people doing repeatable work that software can handle?
            </Callout>

            {/* ── INVOICE EXAMPLE ── */}
            <SectionHeading>What Automation Looks Like in Practice</SectionHeading>
            <Body>
              Let's take a real example: invoice processing. A vendor sends an invoice by email.
              Someone downloads the PDF, reads the invoice number, amount, due date, and vendor
              name, then enters that data into accounting software, asks for approval, and
              schedules payment. Simple — but full of manual steps.
            </Body>

            <SubHeading>Manual Invoice Workflow</SubHeading>

            {/* Diagram 3 — manual invoice */}
            <div className="rv" style={{ margin:'1.5rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid rgba(231,76,60,.35)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="220" viewBox="0 0 900 220" xmlns="http://www.w3.org/2000/svg">
                <rect width="900" height="220" rx="20" fill="#1a0808"/>
                <defs>
                  <marker id="a3" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L8,3 z" fill="#E74C3C" fillOpacity="0.7"/>
                  </marker>
                </defs>
                <text x="450" y="36" textAnchor="middle" fontSize="16" fontFamily="Arial" fill="#f87171" fontWeight="700">❌  Manual Invoice Processing</text>
                {[
                  { label:'Receive\nPDF',    x:40  },
                  { label:'Read\nManually',  x:210 },
                  { label:'Enter Data\nby Hand', x:380 },
                  { label:'Ask for\nApproval',x:550 },
                  { label:'Pay\nInvoice',    x:720 },
                ].map(({ label, x }, i, arr) => (
                  <g key={i}>
                    <rect x={x} y={65} width={145} height={62} rx="10" fill="#2a0a0a" stroke="#E74C3C" strokeWidth="1.5"/>
                    {label.split('\n').map((ln, li) => (
                      <text key={li} x={x+72} y={90 + li*20} textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#fca5a5">{ln}</text>
                    ))}
                    {i < arr.length - 1 && (
                      <line x1={x+145} y1={96} x2={arr[i+1].x} y2={96} stroke="#E74C3C" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#a3)"/>
                    )}
                  </g>
                ))}
                <text x="450" y="178" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#888">Slow, repetitive, and easy to break when volume increases</text>
              </svg>
            </div>

            <SubHeading>Automated Invoice Workflow</SubHeading>
            <Body>
              Now imagine the same process with automation. The invoice arrives, attachment is
              detected, AI extracts the data, validation rules run, the invoice is routed for
              approval, and the accounting system is updated — automatically.
            </Body>

            {/* Diagram 4 — automated invoice */}
            <div className="rv" style={{ margin:'1.5rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid rgba(53,187,234,.35)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="280" viewBox="0 0 900 280" xmlns="http://www.w3.org/2000/svg">
                <rect width="900" height="280" rx="20" fill="#0a1a0e"/>
                <defs>
                  <marker id="a4" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L8,3 z" fill="#35BBEA" fillOpacity="0.7"/>
                  </marker>
                </defs>
                <text x="450" y="36" textAnchor="middle" fontSize="16" fontFamily="Arial" fill="#35BBEA" fontWeight="700">✅  Automated Invoice Workflow</text>
                {[
                  { label:'Invoice\nArrives',   x:50  },
                  { label:'AI/OCR\nExtraction', x:240 },
                  { label:'Validate\nRules',    x:430 },
                  { label:'Approval\nRouting',  x:620 },
                ].map(({ label, x }, i, arr) => (
                  <g key={i}>
                    <rect x={x} y={68} width={155} height={64} rx="10" fill="#0c2318" stroke="#35BBEA" strokeWidth="1.5"/>
                    {label.split('\n').map((ln, li) => (
                      <text key={li} x={x+77} y={93 + li*22} textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#86efac">{ln}</text>
                    ))}
                    {i < arr.length - 1 && (
                      <line x1={x+155} y1={100} x2={arr[i+1].x} y2={100} stroke="#35BBEA" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#a4)"/>
                    )}
                  </g>
                ))}
                {/* accounting output */}
                <path d="M697 132 C697 190 530 190 475 200" stroke="#35BBEA" strokeWidth="1.5" fill="none" strokeOpacity="0.5" markerEnd="url(#a4)"/>
                <rect x="330" y="200" width="260" height="56" rx="12" fill="#0a2a18" stroke="#35BBEA" strokeWidth="2"/>
                <text x="460" y="224" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#eef2ff" fontWeight="700">Accounting System</text>
                <text x="460" y="244" textAnchor="middle" fontSize="12" fontFamily="Arial" fill="#6A99AB">Updated automatically</text>
              </svg>
            </div>

            <Body>
              The accounting system has not been replaced. The inbox has not been replaced. The
              approval process still exists. But the repetitive work has been reduced.
              That is the practical version of automation.
            </Body>

            {/* ── HUMANS VS AUTOMATION ── */}
            <SectionHeading>Automation Removes Friction, Not People</SectionHeading>
            <Body>
              Good automation does not try to remove human judgment from every decision.
              Instead, it separates work into two categories.
            </Body>

            {/* work type table */}
            <div className="rv" style={{
              border:'.5px solid var(--border-hi)', borderRadius:'var(--r)',
              overflow:'hidden', margin:'1.5rem 0', boxShadow:'2px 10px 20px var(--shadow)',
            }}>
              {[
                ['Work Type', 'Best Owner', true],
                ['Repetitive data entry',       'Automation', false],
                ['File movement',               'Automation', false],
                ['Standard validation',         'Automation', false],
                ['Notifications and reminders', 'Automation', false],
                ['Complex judgment',            'Humans',     false],
                ['Exception handling',          'Humans',     false],
                ['Final approval',              'Humans',     false],
                ['Relationship management',     'Humans',     false],
              ].map(([type, owner, isHeader], i) => (
                <div key={i} style={{
                  display:'grid', gridTemplateColumns:'1fr 1fr',
                  background: isHeader ? 'var(--bg)' : i % 2 === 0 ? 'var(--bg2)' : 'var(--surface)',
                  borderBottom: i < 8 ? '1px solid var(--border-s)' : 'none',
                  padding:'.75rem 1.2rem',
                }}>
                  <div style={{ fontSize: isHeader ? '.72rem' : '.9rem', fontWeight: isHeader ? 700 : 500, color: isHeader ? 'var(--muted)' : 'var(--white-70)', letterSpacing: isHeader ? '.1em' : 0, textTransform: isHeader ? 'uppercase' : 'none' }}>{type}</div>
                  <div style={{
                    fontSize: isHeader ? '.72rem' : '.88rem', fontWeight: 700,
                    color: isHeader ? 'var(--muted)' : owner === 'Automation' ? 'var(--blue-hi)' : '#4ade80',
                    letterSpacing: isHeader ? '.1em' : 0, textTransform: isHeader ? 'uppercase' : 'none',
                  }}>{owner}</div>
                </div>
              ))}
            </div>

            <Callout>
              The goal is not to make the business "fully automated." The goal is to let people spend more time on work that actually needs thinking.
            </Callout>

            {/* ── WHERE TO START ── */}
            <SectionHeading>The Best Automation Starts Small</SectionHeading>
            <Body>
              Businesses often delay automation because they imagine a huge transformation project.
              But automation should usually start with one painful workflow. The best first
              automation project is not the most impressive one — it is the one that saves time
              every week and is easy to measure.
            </Body>
            <BulletList items={[
              'Invoice processing',
              'Customer onboarding',
              'Lead qualification',
              'Report generation',
              'Document review',
              'Data syncing between systems',
              'Internal approval workflows',
              'Email-to-task conversion',
              'Inventory alerts',
              'Support ticket routing',
            ]} />

            {/* ── ARCHITECTURE ── */}
            <SectionHeading>A Practical Automation Architecture</SectionHeading>
            <Body>A modern automation setup can be simple — five basic parts that work together without replacing anything you already use.</Body>

            {/* Diagram 5 — architecture */}
            <div className="rv" style={{ margin:'1.8rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid var(--border-hi)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="340" viewBox="0 0 900 340" xmlns="http://www.w3.org/2000/svg">
                <rect width="900" height="340" rx="20" fill="#0c0e14"/>
                <defs>
                  <marker id="a5" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L8,3 z" fill="#6A99AB" fillOpacity="0.7"/>
                  </marker>
                </defs>
                <text x="450" y="38" textAnchor="middle" fontSize="18" fontFamily="Arial" fill="#eef2ff" fontWeight="700">Practical Business Automation Architecture</text>
                {/* top row */}
                {[
                  { label:'Input Sources',     sub:'Email · Forms · PDFs · CRM', x:55,  color:'#35BBEA' },
                  { label:'Automation Engine', sub:'Rules · APIs · Jobs',         x:275, color:'#818cf8' },
                  { label:'AI Layer',          sub:'Extract · Classify · Search', x:495, color:'#f59e0b' },
                ].map(({ label, sub, x, color }) => (
                  <g key={label}>
                    <rect x={x} y={72} width={185} height={70} rx="12" fill="#111520" stroke={color} strokeWidth="1.5"/>
                    <text x={x+92} y={100} textAnchor="middle" fontSize="14" fontFamily="Arial" fill={color} fontWeight="700">{label}</text>
                    <text x={x+92} y={120} textAnchor="middle" fontSize="11" fontFamily="Arial" fill="#8b96b0">{sub}</text>
                  </g>
                ))}
                {/* arrows top row */}
                <line x1={240} y1={107} x2={275} y2={107} stroke="#6A99AB" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#a5)"/>
                <line x1={460} y1={107} x2={495} y2={107} stroke="#6A99AB" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#a5)"/>
                {/* bottom row */}
                {[
                  { label:'Human Review',      sub:'Approvals · Exceptions',      x:175, color:'#4ade80' },
                  { label:'Business Systems',  sub:'ERP · Accounting · Database',  x:530, color:'#35BBEA' },
                ].map(({ label, sub, x, color }) => (
                  <g key={label}>
                    <rect x={x} y={210} width={200} height={70} rx="12" fill="#111520" stroke={color} strokeWidth="1.5"/>
                    <text x={x+100} y={238} textAnchor="middle" fontSize="14" fontFamily="Arial" fill={color} fontWeight="700">{label}</text>
                    <text x={x+100} y={258} textAnchor="middle" fontSize="11" fontFamily="Arial" fill="#8b96b0">{sub}</text>
                  </g>
                ))}
                {/* AI → human review */}
                <path d="M587 142 C587 185 335 185 275 210" stroke="#6A99AB" strokeWidth="1.5" fill="none" strokeOpacity="0.5" markerEnd="url(#a5)"/>
                {/* human → business systems */}
                <line x1={375} y1={245} x2={530} y2={245} stroke="#6A99AB" strokeWidth="1.5" strokeOpacity="0.5" markerEnd="url(#a5)"/>
                {/* AI → business systems direct */}
                <path d="M680 142 C750 142 750 195 730 210" stroke="#6A99AB" strokeWidth="1.5" fill="none" strokeOpacity="0.4" markerEnd="url(#a5)"/>
              </svg>
            </div>

            <SubHeading>1. Input Sources</SubHeading>
            <Body>Where work begins — emails, PDFs, forms, spreadsheets, CRM entries, support tickets, or uploaded documents.</Body>

            <SubHeading>2. Automation Engine</SubHeading>
            <Body>The workflow logic that decides what happens next. If an invoice is below $500, basic approval. Above $5,000, route to finance leadership. If a form is incomplete, send a follow-up email.</Body>

            <SubHeading>3. AI Layer</SubHeading>
            <Body>AI is useful when input is messy — extracting fields from documents, classifying requests, summarizing long messages, detecting missing information. Use AI where rules alone are not enough.</Body>

            <SubHeading>4. Human Review</SubHeading>
            <Body>Automation should include review points where needed. A system extracts invoice data, but a person approves payment. A system summarizes a contract, but a person reviews risk.</Body>

            <SubHeading>5. Existing Business Systems</SubHeading>
            <Body>The final output goes into tools the business already uses — accounting software, CRM, ERP, database, project management tool, or shared drive. This is why automation is powerful: it improves the workflow without forcing the company to abandon current systems.</Body>

            {/* ── IMPACT ── */}
            <SectionHeading>What Changes After Automation</SectionHeading>
            <Body>The biggest benefit is not just speed. It is consistency. Before automation, the process depends heavily on memory and discipline. After automation, the process becomes repeatable.</Body>

            {/* Diagram 6 — before/after */}
            <div className="rv" style={{ margin:'1.8rem 0', borderRadius:'var(--r)', overflow:'hidden', border:'.5px solid var(--border-hi)', boxShadow:'2px 10px 30px var(--shadow)' }}>
              <svg width="100%" height="230" viewBox="0 0 900 230" xmlns="http://www.w3.org/2000/svg">
                <rect width="900" height="230" rx="20" fill="#0c0e14"/>
                <defs>
                  <marker id="a6" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L8,3 z" fill="#6A99AB"/>
                  </marker>
                </defs>
                <text x="450" y="38" textAnchor="middle" fontSize="18" fontFamily="Arial" fill="#eef2ff" fontWeight="700">Business Impact of Automation</text>
                {/* before */}
                <rect x="80" y="68" width="180" height="100" rx="14" fill="#1a0808" stroke="#E74C3C" strokeWidth="2"/>
                <text x="170" y="104" textAnchor="middle" fontSize="17" fontFamily="Arial" fill="#E74C3C" fontWeight="700">Before</text>
                <text x="170" y="130" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#fca5a5">Manual · Slow</text>
                <text x="170" y="150" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#fca5a5">Error-prone</text>
                {/* shift */}
                <rect x="360" y="68" width="180" height="100" rx="14" fill="#1a1608" stroke="#f59e0b" strokeWidth="2"/>
                <text x="450" y="104" textAnchor="middle" fontSize="17" fontFamily="Arial" fill="#f59e0b" fontWeight="700">Add Layer</text>
                <text x="450" y="130" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#fcd34d">Automation</text>
                <text x="450" y="150" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#fcd34d">No replacement</text>
                {/* after */}
                <rect x="640" y="68" width="180" height="100" rx="14" fill="#0a1a0e" stroke="#35BBEA" strokeWidth="2"/>
                <text x="730" y="104" textAnchor="middle" fontSize="17" fontFamily="Arial" fill="#35BBEA" fontWeight="700">After</text>
                <text x="730" y="130" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#86efac">Faster · Consistent</text>
                <text x="730" y="150" textAnchor="middle" fontSize="13" fontFamily="Arial" fill="#86efac">Repeatable</text>
                {/* arrows */}
                <line x1={260} y1={118} x2={360} y2={118} stroke="#6A99AB" strokeWidth="2" markerEnd="url(#a6)"/>
                <line x1={540} y1={118} x2={640} y2={118} stroke="#6A99AB" strokeWidth="2" markerEnd="url(#a6)"/>
                <text x="450" y="198" textAnchor="middle" fontSize="14" fontFamily="Arial" fill="#6A99AB">The business does not just move faster — it becomes more reliable.</text>
              </svg>
            </div>

            <Body>After automation, teams usually see:</Body>
            <BulletList items={[
              'Less manual data entry and fewer missed steps',
              'Faster approvals and cleaner records',
              'Better visibility and easier reporting',
              'More consistent customer experience',
              'More time for higher-value work',
            ]} />

            {/* ── HOW TO INTRODUCE ── */}
            <SectionHeading>The Right Way to Introduce Automation</SectionHeading>
            <Body>A successful automation project should follow a practical path — not a big bang transformation.</Body>

            <div className="rv" style={{ display:'flex', flexDirection:'column', gap:'.9rem', margin:'1.5rem 0' }}>
              {[
                { step:'1', title:'Map the current workflow',     desc:'Before automating anything, understand how the process works today. Who starts it? What data is needed? Which systems are involved? Where do delays and errors happen?' },
                { step:'2', title:'Identify repeatable steps',    desc:'Not everything should be automated. Look for tasks that are frequent, predictable, and rule-based.' },
                { step:'3', title:'Add automation gradually',     desc:'Start with one workflow. Prove value. Then expand. Never try to automate everything at once.' },
                { step:'4', title:'Keep humans in control',       desc:'Automation should support the team, not create a black box. People should be able to review, override, and understand the workflow.' },
                { step:'5', title:'Measure the outcome',         desc:'Track time saved, error reduction, approval time, manual touches removed, and cost per transaction. If it cannot be measured, it is hard to improve.' },
              ].map(({ step, title, desc }) => (
                <div key={step} style={{
                  display:'flex', gap:'1.1rem',
                  background:'var(--bg2)', border:'.5px solid var(--border-hi)',
                  borderRadius:'var(--r)', padding:'1.2rem',
                  boxShadow:'2px 4px 12px var(--shadow)',
                }}>
                  <div style={{
                    width:38, height:38, borderRadius:'50%', flexShrink:0,
                    background:'rgba(53,187,234,.15)', border:'1.5px solid var(--blue-hi)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:'.9rem', fontWeight:700, color:'var(--blue-hi)',
                  }}>{step}</div>
                  <div>
                    <div style={{ fontSize:'.9rem', fontWeight:700, color:'var(--white)', marginBottom:'.3rem' }}>{title}</div>
                    <div style={{ fontSize:'.85rem', fontWeight:500, color:'var(--white-70)', lineHeight:1.65 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* final thought */}
            <div className="rv" style={{
              margin:'3rem 0', padding:'2.5rem 2rem',
              background:'linear-gradient(135deg,var(--bg3),rgba(53,187,234,.06))',
              border:'.5px solid var(--border-hi)', borderRadius:'var(--r)',
              textAlign:'center', boxShadow:'2px 10px 30px var(--shadow)',
              position:'relative', overflow:'hidden',
            }}>
              <div style={{ position:'absolute', top:0, left:'20%', right:'20%', height:2, background:'var(--blue-hi)' }}/>
              <div style={{ fontFamily:'var(--font)', fontSize:'clamp(1rem,2.2vw,1.3rem)', fontWeight:900, color:'var(--white)', marginBottom:'1rem', lineHeight:1.4 }}>
                Automation is not a one-time software purchase.
              </div>
              <div style={{ fontFamily:'var(--font)', fontSize:'clamp(.9rem,1.8vw,1rem)', fontWeight:500, color:'var(--white-70)', lineHeight:1.8, maxWidth:580, margin:'0 auto 1.2rem' }}>
                The smartest companies identify where manual work is slowing the business down,
                then add automation layers that connect existing systems, reduce repetitive work,
                and keep people focused on decisions that matter.
              </div>
              <div style={{ fontFamily:'var(--font)', fontSize:'1rem', fontWeight:700, color:'var(--blue-hi)' }}>
                Build better workflows around the systems and people you already have.
              </div>
            </div>

            {/* author bio */}
            <div className="rv" style={{
              display:'flex', gap:'1.2rem', alignItems:'flex-start',
              padding:'1.5rem', background:'var(--bg2)',
              border:'.5px solid var(--border-hi)', borderRadius:'var(--r)',
              boxShadow:'2px 10px 20px var(--shadow)', marginBottom:'3rem',
            }}>
              <div style={{
                width:52, height:52, borderRadius:'50%', flexShrink:0,
                background:'linear-gradient(135deg,var(--blue-dark),var(--blue-deep))',
                border:'2px solid var(--blue-hi)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'.88rem', fontWeight:700, color:'var(--white)',
              }}>RJ</div>
              <div>
                <div style={{ fontSize:'.92rem', fontWeight:700, color:'var(--white)', marginBottom:'.25rem' }}>Written by Rohit Jain</div>
                <div style={{ fontSize:'.8rem', fontWeight:500, color:'var(--muted)', marginBottom:'.6rem' }}>Founder, arhatinfo.com · Backend Engineer · AI Automation Specialist</div>
                <div style={{ fontSize:'.83rem', fontWeight:500, color:'var(--white-70)', lineHeight:1.7 }}>
                  Building scalable backend systems, automation platforms, cloud-native applications, and AI-driven software solutions.
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="rv" style={{ textAlign:'center' }}>
              <div style={{ fontSize:'.82rem', fontWeight:500, color:'var(--muted)', marginBottom:'1rem' }}>
                Ready to add an automation layer to your business workflows?
              </div>
              <Link to="/contact" className="btn-p">Book a Free Consultation →</Link>
            </div>

          </div>
        </div>
      </article>
    </div>
  );
}

// ── TYPOGRAPHY ────────────────────────────────────────────────────────
function Lead({ children }) {
  return <p style={{ fontSize:'1.05rem', fontWeight:500, color:'rgba(235,242,255,0.88)', lineHeight:1.8, marginBottom:'1.2rem' }}>{children}</p>;
}
function Body({ children }) {
  return <p style={{ fontSize:'.95rem', fontWeight:400, color:'rgba(235,242,255,0.78)', lineHeight:1.85, marginBottom:'1rem' }}>{children}</p>;
}
function SectionHeading({ children }) {
  return (
    <h2 style={{
      fontFamily:'var(--font)', fontSize:'clamp(1.3rem,2.5vw,1.75rem)',
      fontWeight:900, color:'var(--white)',
      marginTop:'2.8rem', marginBottom:'1rem', lineHeight:1.2,
      paddingBottom:'.6rem', borderBottom:'1px solid var(--border-s)',
    }}>{children}</h2>
  );
}
function SubHeading({ children }) {
  return (
    <h3 style={{ fontFamily:'var(--font)', fontSize:'1.05rem', fontWeight:700, color:'var(--blue-hi)', marginTop:'1.8rem', marginBottom:'.6rem' }}>{children}</h3>
  );
}
function Callout({ children }) {
  return (
    <blockquote style={{
      margin:'1.8rem 0', padding:'1.1rem 1.4rem',
      background:'rgba(53,187,234,.06)', borderLeft:'3px solid var(--blue-hi)',
      borderRadius:'0 var(--r) var(--r) 0',
      fontSize:'1rem', fontWeight:600, color:'var(--white)', lineHeight:1.7, fontStyle:'italic',
    }}>{children}</blockquote>
  );
}
function BulletList({ items }) {
  return (
    <ul style={{ listStyle:'none', margin:'.8rem 0 1.2rem', padding:0, display:'flex', flexDirection:'column', gap:'.45rem' }}>
      {items.map((item, i) => (
        <li key={i} style={{ display:'flex', alignItems:'flex-start', gap:'.65rem', fontSize:'.92rem', fontWeight:400, color:'rgba(235,242,255,0.78)', lineHeight:1.65 }}>
          <span style={{ color:'var(--blue-hi)', fontWeight:700, flexShrink:0, marginTop:2 }}>→</span>{item}
        </li>
      ))}
    </ul>
  );
}